/**
 * Schema formatting utilities for NewEntityFilters component
 * Extracted from the main component to improve modularity and testability
 */
import { CROSS_FILTER_ID_SEPARATOR, getCrossID } from 'utils/filters';

/**
 * Formats the final schema for standard filters
 * @param {Object} config - Configuration object
 * @param {Array} config.visibleFilters - Array of visible filter IDs
 * @param {Array} config.quickFilters - Array of quick filter IDs
 * @param {Object} config.mappedSchema - Mapped schema object
 * @param {Array} config.hiddenFilters - Array of hidden filter IDs
 * @param {Function} config.getFieldsFormatted - Function to format fields
 * @param {Function} config.getSubfilterSchema - Function to get subfilter schema
 * @returns {Array} Formatted schema array
 */
export const formatFinalSchema = ({
    visibleFilters,
    quickFilters,
    mappedSchema,
    hiddenFilters,
    getFieldsFormatted,
    getSubfilterSchema,
}) => {
    if (!visibleFilters?.length) return [];

    return visibleFilters
        .filter((filter) => !quickFilters.includes(filter))
        .reduce((arr, current) => {
            if (!mappedSchema?.[current]) return arr;
            if (hiddenFilters.includes(current)) return arr;

            const mappedSchemaItem = mappedSchema[current];

            let isRemovable = true;
            if (mappedSchemaItem?.hasOwnProperty('isRemovable')) {
                isRemovable = mappedSchemaItem.isRemovable;
            }

            let isPinnable = true;
            if (mappedSchemaItem?.hasOwnProperty('isPinnable')) {
                isPinnable = mappedSchemaItem.isPinnable;
            }

            let subfilterSchema;
            if (mappedSchemaItem?.hasOwnProperty('subfilterKey')) {
                subfilterSchema = getSubfilterSchema({
                    filter: current,
                    key: mappedSchemaItem.subfilterKey,
                    schema: mappedSchema,
                });
            }

            arr.push({
                label: mappedSchemaItem.description,
                id: mappedSchemaItem.id,
                filterTooltip: mappedSchemaItem.filterTooltip,
                fields: [...getFieldsFormatted(mappedSchemaItem)],
                isRemovable,
                isPinnable,
                subfilterSchema,
            });

            return arr;
        }, []);
};

/**
 * Formats the final schema for quick filters
 * @param {Object} config - Configuration object
 * @param {Array} config.quickFilters - Array of quick filter IDs
 * @param {Object} config.mappedSchema - Mapped schema object
 * @param {Object} config.crossSchema - Cross schema object
 * @param {Array} config.hiddenFilters - Array of hidden filter IDs
 * @param {Function} config.getFieldsFormatted - Function to format fields
 * @param {Function} config.getSubfilterSchema - Function to get subfilter schema
 * @param {Object} config.crossOperators - Cross operators object
 * @param {Array<string>} [config.readOnlyQuickFilters] - Quick filter ids forced read-only
 * @returns {Array} Formatted quick schema array
 */
export const formatFinalQuickSchema = ({
    quickFilters,
    mappedSchema,
    crossSchema,
    hiddenFilters,
    getFieldsFormatted,
    getSubfilterSchema,
    crossOperators,
    readOnlyQuickFilters = [],
}) => {
    if (!quickFilters || quickFilters.length === 0) return [];

    return quickFilters.reduce((arr, current) => {
        const splitFilter = current.split(CROSS_FILTER_ID_SEPARATOR);
        const isCrossFilter = splitFilter.length > 1;

        let crossEntity, crossID, crossEntityOperators;

        if (isCrossFilter) {
            [crossEntity, crossID] = splitFilter;
            crossEntityOperators = crossOperators?.[crossEntity] || {};
        }

        const finalMappedSchema = isCrossFilter
            ? crossSchema[crossEntity]?.mappedSchema
            : mappedSchema;

        const finalCurrent = isCrossFilter ? crossID : current;

        if (!finalMappedSchema?.[finalCurrent]) return arr;
        if (hiddenFilters.includes(finalCurrent)) return arr;

        let isRemovable = true;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isRemovable')) {
            isRemovable = finalMappedSchema[finalCurrent].isRemovable;
        }

        let isPinnable = true;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isPinnable')) {
            isPinnable = finalMappedSchema[finalCurrent].isPinnable;
        }

        let isFakeCrossFilter = false;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isFakeCrossFilter')) {
            isFakeCrossFilter = finalMappedSchema[finalCurrent].isFakeCrossFilter;
        }

        let isReadOnly = false;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isReadOnly')) {
            isReadOnly = finalMappedSchema[finalCurrent].isReadOnly;
        }
        if (readOnlyQuickFilters.includes(finalCurrent)) {
            isReadOnly = true;
        }

        let subfilterSchema;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('subfilterKey')) {
            subfilterSchema = getSubfilterSchema({
                crossEntity,
                filter: finalCurrent,
                key: finalMappedSchema[finalCurrent].subfilterKey,
                schema: finalMappedSchema,
            });
        }

        const filterObj = {
            label: finalMappedSchema[finalCurrent].description,
            id: finalMappedSchema[finalCurrent].id,
            filterTooltip: finalMappedSchema[finalCurrent].filterTooltip,
            fields: [...getFieldsFormatted(finalMappedSchema[finalCurrent], crossEntityOperators)],
            isRemovable,
            isPinnable,
            isFakeCrossFilter,
            isReadOnly,
            subfilterSchema,
        };

        if (isCrossFilter) {
            filterObj.subLabel = crossSchema[crossEntity]?.title;
            filterObj.entity = crossEntity;
            filterObj.crossId = getCrossID(finalMappedSchema[finalCurrent].id, crossEntity);
        }

        arr.push(filterObj);

        return arr;
    }, []);
};

/**
 * Formats the final cross schema
 * @param {Object} config - Configuration object
 * @param {Object} config.crossSchema - Cross schema object
 * @param {Function} config.getFinalCrossSchema - Function to get final cross schema
 * @param {Array} config.quickfilters - Mapped quick filters
 * @param {Array} config.visibleFilters - Mapped visible filters
 * @param {Function} config.getFieldsFormatted - Function to format fields
 * @param {Function} config.getSubfilterSchema - Function to get subfilter schema
 * @returns {Object} Formatted cross schema object
 */
export const formatFinalCrossSchema = ({
    crossSchema,
    getFinalCrossSchema,
    quickfilters,
    visibleFilters,
    getFieldsFormatted,
    getSubfilterSchema,
}) => {
    if (!crossSchema || Object.entries(crossSchema).length === 0) return {};

    return getFinalCrossSchema({
        crossSchema,
        quickfilters,
        visibleFilters,
        getFieldsFormatted,
        getSubfilterSchema,
    });
};
